import React from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  Skeleton,
  Paper,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import { Link } from 'react-router-dom';
import { brandColors } from '../../theme';
import { useFeatureUpdates, useArchivedIdeas } from '../../hooks/useIdeaBoard';
import FeatureUpdateEntry from '../../components/idea-board/FeatureUpdateEntry';
import ArchivedIdeaCard from '../../components/idea-board/ArchivedIdeaCard';

const RecentUpdatesPage: React.FC = () => {
  const {
    data: featureUpdates,
    isLoading: updatesLoading,
    isError: updatesError,
  } = useFeatureUpdates(20);

  const {
    data: archivedIdeas,
    isLoading: archivedLoading,
    isError: archivedError,
  } = useArchivedIdeas();

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
      {/* Back navigation */}
      <Box sx={{ mb: 3 }}>
        <Button
          component={Link}
          to="/idea-board"
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none', color: brandColors.blue, pl: 0 }}
        >
          Back to Idea Board
        </Button>
      </Box>

      {/* Page title */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: brandColors.indigo, mb: 0.75 }}
        >
          Recent Updates
        </Typography>
        <Typography variant="body1" sx={{ color: brandColors.mediumGray }}>
          See what's been shipped and which ideas have been addressed.
        </Typography>
        <Divider sx={{ mt: 2 }} />
      </Box>

      {/* Feature Updates Section */}
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <NewReleasesOutlinedIcon sx={{ color: brandColors.gold }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: brandColors.indigo }}>
            Feature Updates
          </Typography>
        </Box>

        {updatesLoading && (
          <Box>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ display: 'flex', gap: 2, py: 2 }}>
                <Skeleton variant="circular" width={36} height={36} sx={{ flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="30%" sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="90%" />
                  <Skeleton variant="text" width="70%" />
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {updatesError && !updatesLoading && (
          <Alert severity="error">Failed to load feature updates.</Alert>
        )}

        {!updatesLoading && !updatesError && (
          <>
            {featureUpdates && featureUpdates.length > 0 ? (
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', px: 2 }}>
                {featureUpdates.map((update, index) => (
                  <FeatureUpdateEntry
                    key={update.id}
                    update={update}
                    showDivider={index < featureUpdates.length - 1}
                  />
                ))}
              </Paper>
            ) : (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 5,
                  color: brandColors.mediumGray,
                  border: `1px dashed`,
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <NewReleasesOutlinedIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1 }} />
                <Typography variant="body2">No feature updates yet.</Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Archived Ideas Section */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <ArchiveOutlinedIcon sx={{ color: brandColors.indigo }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: brandColors.indigo }}>
            Completed Ideas
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: brandColors.mediumGray, mb: 2 }}>
          Ideas from the last 3 months that have been addressed.
        </Typography>

        {archivedLoading && (
          <Box>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={90} sx={{ mb: 1.5 }} />
            ))}
          </Box>
        )}

        {archivedError && !archivedLoading && (
          <Alert severity="error">Failed to load completed ideas.</Alert>
        )}

        {!archivedLoading && !archivedError && (
          <>
            {archivedIdeas && archivedIdeas.length > 0 ? (
              archivedIdeas.map((idea) => (
                <ArchivedIdeaCard key={idea.id} idea={idea} />
              ))
            ) : (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 5,
                  color: brandColors.mediumGray,
                  border: `1px dashed`,
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <ArchiveOutlinedIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1 }} />
                <Typography variant="body2">No completed ideas in the last 3 months.</Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default RecentUpdatesPage;
